// Per-turn dollar cost estimator. Static pricing table — keep in sync
// with Anthropic's published rates when prices change (or when a new
// model joins MODEL_OPTIONS). Numbers are public and deliberately not
// treated as secret.
//
// Cache multipliers (ephemeral, 5-min TTL):
//   - reads:  ~0.1×  input_rate
//   - writes: ~1.25× input_rate
// Longer TTL (1h) doubles the write rate, but we don't use 1h caching.

import type { UsageSummary } from './client';
import type { ModelId } from './storage';

type Price = {
  /** Dollars per 1M uncached input tokens. */
  input: number;
  /** Dollars per 1M output tokens. */
  output: number;
};

const PRICES: Record<ModelId, Price> = {
  'claude-opus-4-7': { input: 5, output: 25 },
  'claude-opus-4-6': { input: 5, output: 25 },
  'claude-sonnet-4-6': { input: 3, output: 15 },
  'claude-haiku-4-5': { input: 1, output: 5 },
};

const CACHE_READ_MULT = 0.1;
const CACHE_WRITE_MULT_EPHEMERAL = 1.25;

/** Return the USD cost of a turn as a finite number. */
export function costOf(model: ModelId, usage: UsageSummary): number {
  const p = PRICES[model];
  const perM = 1_000_000;
  const input = (usage.inputTokens * p.input) / perM;
  const cacheRead =
    (usage.cacheReadTokens * p.input * CACHE_READ_MULT) / perM;
  const cacheWrite =
    (usage.cacheWriteTokens * p.input * CACHE_WRITE_MULT_EPHEMERAL) / perM;
  const output = (usage.outputTokens * p.output) / perM;
  return input + cacheRead + cacheWrite + output;
}

/** Format a dollar amount at a precision useful for per-turn costs.
 *  Sub-cent amounts show 4 decimals ($0.0013); above a cent show 3
 *  ($0.041); above a dollar show 2 ($1.23). */
export function formatCost(usd: number): string {
  if (!Number.isFinite(usd) || usd < 0) return '$0.00';
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  if (usd >= 0.01) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(4)}`;
}
